/**
 * My metrics — one page for "me" and "my team" (Shayna, review #59, 2026-10-05).
 *
 * "combining my metrics and my teams metrics to My Metrics and whoever logs in it will
 * display the correct info. for a supervisor view you can toggle between just me and
 * my team. for executive and admin view we keep a person picker."
 *
 *  - It opens on the person signed in (`Dataset.viewer`, set by /api/dataset from
 *    their FCCKey), where their response carries their row. Otherwise — in dev, or for
 *    someone with no row — on the first person carrying claims, as before.
 *  - "Viewing as" picks anybody the caller may see: every person in scope, and every
 *    supervisor, including managers with no row of their own (who have only a team).
 *  - Someone who leads a team gets "My team | Just me". My team is <TeamDetail>, as
 *    the Supervisors sidebar shows it; Just me is <PersonDetail>, as the drill-through
 *    shows it. Nobody else gets the toggle: they only have "me".
 *
 * Replaces "My team's metrics" (/supervisor-view), which now redirects here on the
 * team view. Picking a person here is the header's Employee filter, as it always was;
 * a manager with no row is kept on the page instead, since the filter lists people.
 */
import { useMemo } from 'react'
import type { CSSProperties } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAppData } from '@/data/AppData'
import { useLoadedDataset } from '@/data/DatasetProvider'
import { useFilters, ALL } from '@/data/filters'
import { PersonDetail } from '@/components/PersonDetail'
import { SearchPick } from '@/components/SearchPick'
import { TeamDetail } from '@/components/TeamDetail'
import { useUrlState, list, oneOf, text } from '@/ui/urlState'
import { MyStatements } from '@/components/MyStatements'

type View = 'team' | 'me'

export function EmployeeView() {
  const { derived, scope, openMonths, openPerson } = useAppData()
  const dataset = useLoadedDataset()
  const { filters, set } = useFilters()
  /** My team or Just me, for someone who leads a team. In the URL: it is not a name. */
  const [view, setView] = useUrlState<View>('view', 'team', oneOf(['team', 'me'] as const, 'team'))
  /** A manager with no row of their own, picked here; names stay out of the URL. */
  const [local, setLocal] = useUrlState('who', '', text, { personal: true })
  /** What "the team" means for every figure on the team view. */
  const [depth, setDepth] = useUrlState('show', 'tree', oneOf(['tree', 'direct'] as const, 'tree'))
  /** Supervisors expanded in "My people". */
  const [openList, setOpenList] = useUrlState('open', [] as string[], list, { personal: true })

  // Everyone anybody reports to, at any level — read past the Employee filter, so
  // picking someone never hides the teams. A manager of managers is a supervisor here
  // even where nobody in this data reports to them directly.
  const supervisors = useMemo(() => {
    const names = new Set<string>()
    for (const p of scope.reportPool) for (const s of p.supervisors) if (s !== p.employee) names.add(s)
    return names
  }, [scope.reportPool])

  // Everyone in scope, claims or not — a person paid in the period with no billed
  // claims is still someone to look up — and the managers with no row.
  const people = useMemo(
    () => [...scope.reportPool].sort((a, b) => a.employee.localeCompare(b.employee)),
    [scope.reportPool],
  )
  const names = useMemo(() => {
    const all = new Set(people.map((p) => p.employee))
    for (const s of supervisors) all.add(s)
    return [...all].sort((a, b) => a.localeCompare(b))
  }, [people, supervisors])

  const me = dataset.viewer?.employee ?? null
  const mine = me && names.includes(me) ? me : null
  // The header's filters win, so the page and the filter bar never disagree about
  // who is on screen; then a manager picked here; then whoever is signed in.
  const picked =
    filters.employee !== ALL
      ? filters.employee
      : filters.supervisor !== ALL
        ? filters.supervisor
        : local || mine || (people.find((p) => p.claims > 0) ?? people[0])?.employee || ''

  const person = picked ? (scope.byName.get(picked) ?? null) : null
  const leads = supervisors.has(picked)
  // A manager with no row has only a team; someone who leads nobody has only "me".
  const shown: View = !leads ? 'me' : !person ? 'team' : view

  const pick = (v: string) => {
    setOpenList([])
    if (!v) {
      setLocal('')
      set('employee', ALL)
    } else if (scope.byName.has(v)) {
      setLocal('')
      set('employee', v)
    } else {
      // Not a person the Employee filter lists: keep them here instead.
      set('employee', ALL)
      if (filters.supervisor !== ALL) set('supervisor', v)
      setLocal(v)
    }
  }

  const header = (
    <>
      <div style={{ width: 380, maxWidth: '100%' }}>
        <SearchPick
          label="Viewing as"
          lead
          value={picked}
          allValue=""
          allLabel="Pick a person"
          options={names.map((n) => ({ value: n, label: n === mine ? `${n} (you)` : n }))}
          onChange={pick}
        />
      </div>
      <MyStatements />
      {leads && person && (
        <div style={{ display: 'grid', gap: 4 }}>
          <span className="eyebrow">Show</span>
          <div role="radiogroup" aria-label="My team or just me" style={seg}>
            {(
              [
                ['team', 'My team'],
                ['me', 'Just me'],
              ] as const
            ).map(([value, label]) => {
              const on = shown === value
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setView(value)}
                  style={{
                    ...segBtn,
                    background: on ? 'var(--accent)' : 'transparent',
                    color: on ? 'var(--on-accent, #fff)' : 'var(--ink-2)',
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </>
  )

  if (shown === 'team')
    return (
      <TeamDetail
        supervisor={picked}
        depth={depth}
        onDepth={setDepth}
        openList={openList}
        onOpenList={setOpenList}
        onOpenPerson={(name) => openPerson(name)}
        picker={header}
        // Whole teams, whoever is picked: the Employee filter chooses whose page this
        // is, never who is on their team.
        pool={scope.reportPool}
        rankPool={scope.reportRankPool}
      />
    )

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
        {header}
      </div>

      <div style={{ marginTop: 20 }}>
        {person ? (
          <PersonDetail person={person} derived={derived} openMonths={openMonths} />
        ) : (
          <div className="card" style={{ borderLeft: '3px solid var(--accent)' }}>
            <p className="body-note" style={{ margin: 0 }}>
              Nobody matches the current filters. Widen the scope above, or reset the
              filter bar.
            </p>
          </div>
        )}
      </div>
    </>
  )
}

/**
 * The old "My team's metrics" address, kept so bookmarks and review comments still
 * land: My metrics on the team view, with the rest of the query string kept.
 */
export function SupervisorViewRedirect() {
  const { search } = useLocation()
  const q = new URLSearchParams(search)
  q.set('view', 'team')
  return <Navigate to={`/employee-view?${q.toString()}`} replace />
}

const seg: CSSProperties = {
  display: 'inline-flex',
  border: '1px solid var(--rule-2)',
  borderRadius: 6,
  overflow: 'hidden',
  background: 'var(--surface)',
}

const segBtn: CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 'var(--t-table)',
  padding: '5px 12px',
  border: 0,
  cursor: 'pointer',
}
